/**
 * This is a reducer for keeping track of all the loaded reponses and their edited data.
 *
 * The plan is to only load the response data that we need. All other response data will
 * only live on the API.
 *
 * The reducer state will be a map of response ID's to response records:
 *
 * Map {
 *   [response_id]: [ResponseRecord]
 *   ...
 * }
 *
 * This should have much greater performance benefits, rather than loading ALL
 * responses wit ALL the nested data insite them.
 *
 */

import * as Immutable from "immutable";
import { BotSwitchMessageRecord } from "reducers/responses/messageRecords";
import {
  createMessageRecord,
  createRecordFromResponse
} from "reducers/responses/reducer";
import {
  deleteVariableFromMessage,
  attachVariableToMessage,
  updateVariableIdsFromServer
} from "./variablesHelpers";
import {
  BaseSuggestedResponse,
  getMessageIndex,
  stateWithMetaData
} from "./reducerHelpers";

let lastSavedResponses = Immutable.Map();

/**
 *
 * @param {Immutable.Map} state
 * @param {Object} action
 */
export const responsesLoaded = (state = Immutable.Map(), action) => {
  const actionResponseId = action.responseId;
  let nextState = state;
  let newResponse;
  let path;
  let message;

  switch (action.type) {
    // After we've fetched a response from API,
    // insert it into the Map.
    case "GET_RESPONSES_BY_ID_SUCCESS":
      // eslint-disable-next-line prefer-destructuring
      newResponse = createRecordFromResponse(
        action.response.data.response,
        undefined
      );

      // Update lastSavedResponses Map
      lastSavedResponses = lastSavedResponses.set(newResponse.id, newResponse);

      return nextState.set(newResponse.id, newResponse);

    case "SAVE_RESPONSE_SUCCESS":
      newResponse = createRecordFromResponse(
        action.response.data.response,
        nextState.get(actionResponseId)
      );

      nextState = nextState.set(actionResponseId, newResponse);

      // Update lastSavedResponses Map
      lastSavedResponses = lastSavedResponses.set(newResponse.id, newResponse);

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

      /** ************** */
      /** CREATE ACTIONS */
      /** ************** */

    case "CREATE_RESPONSE_SUCCESS":
      if (!action.response.data.response._id) {
        return state;
      }

      newResponse = createRecordFromResponse(
        action.response.data.response,
        nextState.get(action.response.data.response._id)
      );

      lastSavedResponses = lastSavedResponses.set(newResponse.id, newResponse);

      nextState = nextState.set(action.response.data.response._id, newResponse);

      return stateWithMetaData(
        action.response.data.response._id,
        lastSavedResponses,
        nextState
      );

    case "CREATE_RESPONSE_MESSAGE": {
      const { language } = action;
      message = createMessageRecord({
        type: action.messageType,
        displayType: action.messageDisplayType
      });

      nextState = nextState.updateIn([actionResponseId, "messages", language], array => (
        array.slice(0, action.index).push(message).concat(array.slice(action.index))
      ));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "CREATE_NESTED_RESPONSE_MESSAGE": {
      const { droppable, language } = action;

      message = createMessageRecord({
        type: action.messageType,
        displayType: action.messageDisplayType
      });

      const messageIndex = getMessageIndex(
        state.getIn([actionResponseId, "messages", language]),
        action.messageId
      );

      nextState = nextState.updateIn([actionResponseId, "messages", language, messageIndex, ...droppable], array => (
        array.slice(0, action.index).push(message).concat(array.slice(action.index))
      ));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "PASTE_RESPONSE_MESSAGE": {
      const { language } = action;
      const messagesFromClipboard = action.messages;
      const copiedMessageRecords = messagesFromClipboard.map(
        (messageFromClipboard) => {
          const messageFromClipboardCopy = messageFromClipboard;
          delete messageFromClipboardCopy.id;
          delete messageFromClipboardCopy.invalidFields;

          return createMessageRecord(
            Immutable.fromJS(messageFromClipboardCopy), false
          );
        }
      );

      nextState = nextState.updateIn([actionResponseId, "messages", language], (array) => {
        const newArray = array
          .slice(0, action.index)
          .concat(copiedMessageRecords);

        return newArray
          .concat(array.slice(action.index));
      });

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    /** *********************** */
    /** UPDATE RESPONSE ACTIONS */
    /** *********************** */

    case "UPDATE_RESPONSE":
      nextState = nextState.mergeIn([actionResponseId], action.payload);

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "UPDATE_RESPONSE_ATTRIBUTES": {
      const payload = action.newResourceRecord.changedAttributes;

      return stateWithMetaData(
        action.id,
        lastSavedResponses,
        nextState.mergeIn([action.id], payload)
      );
    }

    case "UPDATE_RESPONSE_STATUS_REQUEST":
    case "UPDATE_RESPONSE_STATUS_SUCCESS":
      nextState = nextState.mergeIn([actionResponseId], { live: action.responseStatus });

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "UPDATE_RESPONSE_MESSAGE":
      path = action.keyPath;
      path[0] = actionResponseId;

      if (action.deepPath) {
        path = path.concat(action.deepPath);
      }

      nextState = nextState.mergeIn(
        path,
        action.val
      );

      message = nextState.getIn(action.keyPath);

      nextState = nextState.mergeIn(
        action.keyPath,
        message
      );

      return stateWithMetaData(actionResponseId, lastSavedResponses, nextState);

    case "UPDATE_IN_RESPONSE_MESSAGE": {
      const { keypath, val } = action;
      path = action.keyPath;
      path[0] = actionResponseId;

      nextState = nextState.mergeIn(
        keypath,
        val
      );

      nextState = nextState.mergeIn(
        keypath.slice(0, 4),
        nextState.getIn(keypath.slice(0, 4))
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "UNDO_RESPONSE": {
      const revertedResponse = lastSavedResponses.get(actionResponseId);

      nextState = nextState.setIn([actionResponseId], revertedResponse);

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    /** ************************ */
    /** REORDER MESSAGES ACTIONS */
    /** ************************ */

    case "REORDER_RESPONSE_MESSAGES":
      nextState = nextState.updateIn(
        [actionResponseId, "messages", action.language],
        (arr) => {
          const movedMessage = arr.getIn([action.spliceFrom]);

          return arr
            .splice(action.spliceFrom, 1)
            .splice(action.spliceTo, 0, movedMessage);
        }
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "REORDER_NESTED_RESPONSE_MESSAGES": {
      const messageIndex = getMessageIndex(nextState.getIn([actionResponseId, "messages", action.language]), action.messageId);

      nextState = nextState.updateIn(
        [actionResponseId, "messages", action.language, messageIndex, ...action.messagesKey],
        (arr) => {
          const movedMessage = arr.getIn([action.spliceFrom]);

          return arr
            .splice(action.spliceFrom, 1)
            .splice(action.spliceTo, 0, movedMessage);
        }
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    /** ************** */
    /** DELETE ACTIONS */
    /** ************** */

    case "DELETE_RESPONSE":
      return nextState.delete(action.responseId);

    case "DELETE_RESPONSE_MESSAGE":
      path = action.keyPath;
      path[0] = actionResponseId;

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        state.deleteIn(path)
      );

      /** ***************** */
      /** HTTP TEST ACTIONS */
      /** ***************** */

    case "POST_TEST-HTTP_REQUEST":
      path = action.keyPath;
      path[0] = actionResponseId;

      return nextState.setIn(
        [
          ...path,
          "httpResponseLoading"
        ],
        true
      );

    case "POST_TEST-HTTP_SUCCESS":
    case "POST_TEST-HTTP_FAILURE":
      path = action.keyPath;
      path[0] = actionResponseId;
      nextState = nextState.setIn(
        [
          ...path,
          "httpResponseLoading"
        ],
        false
      );

      return nextState.setIn(
        [
          ...path,
          "httpResponse",
          action.requestType
        ],
        action.response
      );

      /** ****************************************** */
      /** SUGGESTED RESPONSE MESSAGE REDUCER ACTIONS */
      /** ****************************************** */

    case "ADD_SUGGESTED_RESPONSE": {
      const optionToPush = BaseSuggestedResponse(action);

      if (nextState.getIn([actionResponseId, "options"]).has(action.language)) {
        nextState = nextState.updateIn(
          [actionResponseId, "options", action.language],
          arr => arr.push(optionToPush)
        );
      } else {
        nextState = nextState.setIn(
          [actionResponseId, "options", action.language],
          Immutable.List([optionToPush])
        );
      }

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "DELETE_SUGGESTED_RESPONSE":
      nextState = nextState.updateIn([actionResponseId, "options", action.language], arr => (
        arr.filter(option => option.get("target") !== action.responseIdToDelete)
      ));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "REORDER_SUGGESTED_RESPONSES":
      nextState = nextState.updateIn(
        [actionResponseId, "options", action.language],
        (selectedResponses) => {
          const movedOption = selectedResponses.getIn([action.spliceFrom]);

          return selectedResponses
            .splice(action.spliceFrom, 1)
            .splice(action.spliceTo, 0, movedOption);
        }
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "REORDER_QUICK_REPLIES_BLOCK": {
      nextState = nextState.updateIn(
        [action.responseId, "messages", action.language, ...action.keyPath, "quickReplies"],
        (quickReplies) => {
          const movedOption = quickReplies.getIn([action.spliceFrom]);

          return quickReplies
            .splice(action.spliceFrom, 1)
            .splice(action.spliceTo, 0, movedOption);
        }
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "UPDATE_SUGGESTED_RESPONSE": {
      const optionsArray = nextState.getIn([
        actionResponseId,
        "options",
        action.language
      ]);

      const optionIndex = optionsArray.findIndex(
        option => option.get("target") === action.responseIdToModify
      );
      nextState = nextState.setIn(
        [actionResponseId, "options", action.language, optionIndex, "label"],
        action.value || null
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "SET_RESPONSE_BUTTON": {
      const { buttonLabel } = action.payload;

      /*
        Default clarification language to english. May want to change this to
        reflect a default language
      */
      const buttonLanguageToSet = action.payload.languageCode;

      nextState = nextState.setIn([actionResponseId, "buttonLabel", buttonLanguageToSet], buttonLabel);

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "DELETE_RESPONSE_BUTTON_LANGUAGE": {
      const { responseId, languageCode } = action;

      nextState = nextState.deleteIn([responseId, "buttonLabel", languageCode]);

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    /** **************** */
    /** LANGUAGE ACTIONS */
    /** **************** */

    case "ADD_LANGUAGE_TO_RESPONSE_MESSAGES": {
      const isBotSwitchResponse = !nextState.getIn([
        actionResponseId,
        "canAddMessages"
      ]);

      const messages = isBotSwitchResponse
        ? Immutable.List([
          new BotSwitchMessageRecord()
        ])
        : action.messages;

      nextState = nextState
        .setIn([actionResponseId, "messages", action.language], messages)
        .setIn([actionResponseId, "options", action.language], Immutable.List([]));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "ADD_BUTTON_LABEL_LANGUAGE_TO_RESPONSE":
      nextState = nextState
        .setIn([actionResponseId, "buttonLabel", action.language], "");

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "DELETE_LANGUAGE": {
      const { responseId, languageCode } = action;

      nextState = nextState
        .deleteIn([responseId, "messages", languageCode])
        .deleteIn([responseId, "options", languageCode]);

      return stateWithMetaData(
        responseId,
        lastSavedResponses,
        nextState
      );
    }

    // variables
    case "CREATE_UNSAVED_VARIABLE":
      // falls through

    case "SET_DROPDOWN_VARIABLE": {
      path = action.keyPath;
      path[0] = actionResponseId;
      nextState = attachVariableToMessage(state, action, path);

      nextState = nextState.setIn(
        path,
        nextState.getIn(path)
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "UPDATE_VARIABLE": {
      path = action.keyPath;
      path[0] = actionResponseId;

      if (action.existingVar) {
        nextState = nextState.updateIn(path, (msg) => {
          let transformedMsg = msg;

          if (msg.type === "capture" || msg.type === "list_selection_template") {
            transformedMsg = transformedMsg.set("variableId", action.existingVar.id);
          } else if (msg.type === "http_request_recipe" || msg.type === "variable_override" || msg.type === "widget") {
            transformedMsg = transformedMsg.update("variablesData", variables => variables.map(
              data => (data.get("variableId") === action.variable.id ? data.set("variableId", action.existingVar.id) : data)
            ));

            transformedMsg = transformedMsg.update("variablesData", variablesData => variablesData.map(
              (data) => {
                if (data.get("variableId") !== action.variable.id) {
                  return data;
                }

                return data.merge({ variableId: action.existingVar.id });
              }
            ));
          }

          return transformedMsg;
        });
      }

      nextState = nextState.setIn(
        path,
        nextState.getIn(path)
      );

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "DELETE_VARIABLE": {
      path = action.keyPath;
      path[0] = actionResponseId;
      const messagePath = path;

      nextState = deleteVariableFromMessage(nextState, action, messagePath);
      nextState = nextState.setIn(
        messagePath,
        nextState.getIn(messagePath)
      );


      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );
    }

    case "CREATE_VARIABLE_SUCCESS": {
      if (
        action.variable.id !== action.response.data.variable._id
      ) {
        nextState = updateVariableIdsFromServer(nextState, action);

        return stateWithMetaData(
          actionResponseId,
          lastSavedResponses,
          nextState
        );
      }

      return nextState;
    }

    /** *********** */
    /** TAG ACTIONS */
    /** *********** */
    case "REMOVE_TAG":
      nextState = nextState.updateIn([actionResponseId, "tags"], arr => arr.filter(tagId => tagId !== action.tagId));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    case "ADD_TAG_TO_RESPONSE":
      nextState = nextState.updateIn([actionResponseId, "tags"], arr => arr.push(action.tagId));

      return stateWithMetaData(
        actionResponseId,
        lastSavedResponses,
        nextState
      );

    default:
      return nextState;
  }
};
